/**
 * Creator Studio's Voice and music controls.
 *
 * Moving a control writes three values onto the story timeline: the language,
 * which voice is heard, and whether the recording's own music plays under it.
 * It creates no audio and no tracks. The dubbed audio is worked out by the dub
 * engine every time the timeline is turned into a document, so it can never
 * drift out of step with a picture that has been trimmed or cut (PLAT-11511).
 *
 * One press is therefore one command with nothing on the network behind it, and
 * one Undo press takes it back.
 *
 * Both positions are read back from the timeline rather than held as the
 * screen's own copy, so a saved story reopens with the controls where the
 * timeline actually is, and Undo moves them back.
 */
import { SetDubOptionsCommand } from '@src/editor/commands/SetDubOptionsCommand';
import { normalizeDubVoiceVolume } from '@src/editor/dubbing/DubEngine';
import {
  useEditor,
  useUserIdentity,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useEffect, useState } from 'react';

import { STORY_DUB_APPLY_MESSAGES } from './dubApplyMessages';
import { type DubLevels, FULL_DUB_LEVEL } from './DubLevelSlider';
import {
  TIMELINE_MAY_HAVE_MOVED,
  useAppliedStoryDub,
} from './useAppliedStoryDub';
import { useDubbedLanguagesRead } from './useDubbedLanguages';
import { useDubDescriptionRefresh } from './useDubDescriptionRefresh';
import { useVoiceActivation } from './useVoiceActivation';

import type { AudioMode } from './AudioModeSegmentedToggle';
import type { MusicSeparationController } from './useMusicSeparationController';
import type { DubMusicMode } from '@src/editor/dubbing/DubEngine';
import type { EditorObserver } from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('component:useCreatorStudioDubComposition');

/** The story timeline's music position and dub levels, read together. */
interface StoryMix {
  musicMode: DubMusicMode;
  voiceVolume: number | undefined;
  originalVolume: number | undefined;
}

export interface UseCreatorStudioDubCompositionOptions {
  /** The transcript's selected language. `null` = Original. */
  selectedLanguage: string | null;
  /**
   * The story's voice/music separation state. Two things are read from it:
   * whether its first state read has come back, and the kept bed's signed
   * address.
   */
  musicController?: MusicSeparationController;
}

export interface CreatorStudioDubCompositionControl {
  /** The mode the control paints as selected, READ FROM THE TIMELINE. */
  audioMode: AudioMode;
  /** The language whose dub is on the story timeline, or `null` when none is. */
  appliedDubLanguage: string | null;
  /**
   * The language the control is SHOWING — the applied dub's when the timeline
   * carries one and the selected language has no dub of its own, the selection
   * otherwise. Every press acts on this one value, so what is shown and what is
   * done cannot disagree.
   */
  controlLanguage: string | null;
  /**
   * A read a press decides from has not arrived yet: the dubbed-language list,
   * or the separation state. The control paints as working and refuses presses
   * while set.
   */
  audioModeBusy: boolean;
  /** `undefined` when the AI-Voices flag or the showcase's dubbing setting is off. */
  onAudioModeChange: ((mode: AudioMode) => void) | undefined;
  /**
   * Puts the music bed on the story timeline, or takes it off — the same
   * command as the Voice control, with the voice position left where the
   * timeline has it.
   */
  onMusicModeChange: ((mode: DubMusicMode) => void) | undefined;
  /** The music position the control paints as selected, READ FROM THE TIMELINE. */
  musicMode: DubMusicMode;
  /** PLAT-11899 — the dubbed voice's level, 0–100, READ FROM THE TIMELINE. */
  voiceVolume: number;
  /**
   * PLAT-11899 — save a new level, one undo step, leaving the language and both
   * positions where the timeline has them. `undefined` when dubbing is off.
   */
  onVoiceVolumeChange: ((level: number) => void) | undefined;
  /** PLAT-11927 — the original recording's level on Both, READ FROM THE TIMELINE. */
  originalVolume: number;
  /** PLAT-11927 — save the original's level, as `onVoiceVolumeChange` does. */
  onOriginalVolumeChange: ((level: number) => void) | undefined;
  /** PLAT-11927 — save both levels as one undo step; one left out is kept. */
  onLevelsChange: ((levels: Partial<DubLevels>) => void) | undefined;
  /** The BCP-47 codes that already have a dub for this story. */
  dubbedLanguages: string[];
  /**
   * Fetch the dub description again, leaving both control positions where they
   * are. For a run that changed the dub itself on the server: the audio in hand
   * was built from the manifest as it was before, so without this the timeline
   * goes on playing silence over lines that now have a voice.
   *
   * `undefined` on a workspace with dubbing switched off.
   */
  reapplyComposition: (() => void) | undefined;
  /**
   * Read the story's dubbed-language list again. For the run that ADDS a
   * language to it — the list is read once when the surface opens, so a first
   * dub would otherwise leave the Voice control absent until a reload.
   *
   * `undefined` on a workspace with dubbing switched off.
   */
  refreshDubbedLanguages: (() => void) | undefined;
}

export function useCreatorStudioDubComposition({
  selectedLanguage,
  musicController,
}: UseCreatorStudioDubCompositionOptions): CreatorStudioDubCompositionControl {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();
  const videoSourceStore = useVideoSource();
  const userIdentity = useUserIdentity();
  const flags = useFlagsCombination(userIdentity.currentRole);
  const { dubbingEnabled } = useVoiceActivation();
  const dubEntryEnabled = !!flags.enableAiVoices && dubbingEnabled;
  const storyId = videoCreator.storyId ?? null;

  const {
    languages: dubbedLanguages,
    settled: dubbedLanguagesSettled,
    refresh: refreshDubbedLanguages,
  } = useDubbedLanguagesRead(storyId);

  // Whether the separation state is an ANSWER yet. A press landing before it
  // would write "this story has no music" onto a story that has some.
  const musicStateSettled = musicController
    ? musicController.status !== 'loading'
    : true;

  // Read through the same hook the publish voice notice uses, so the control
  // and the notice cannot hold different answers on the same screen.
  const { appliedDub, resync: resyncAppliedDub } = useAppliedStoryDub();

  // Every press writes every value at once, so without this read a press
  // would write `none` and quietly clear a bed the person asked for — or, since
  // PLAT-11899, write no level and quietly put the dub back to full.
  const readStoryMix = useCallback((): StoryMix => {
    const options = editorStore.editor?.timeline.dubOptions;
    return {
      musicMode: options?.musicMode ?? 'none',
      voiceVolume: options?.voiceVolume,
      originalVolume: options?.originalVolume,
    };
  }, [editorStore]);

  const [appliedMix, setAppliedMix] = useState<StoryMix>(readStoryMix);
  const appliedMusic = appliedMix.musicMode;

  // Re-renders on a changed answer only, so an unrelated timeline edit does not
  // repaint the panel.
  const resyncAppliedMusic = useCallback(() => {
    setAppliedMix(previous => {
      const next = readStoryMix();
      return previous.musicMode === next.musicMode &&
        previous.voiceVolume === next.voiceVolume &&
        previous.originalVolume === next.originalVolume
        ? previous
        : next;
    });
  }, [readStoryMix]);

  useEffect(() => {
    // A story loads after this hook first renders, so this subscription is what
    // makes a saved story come back with the music line where the timeline
    // actually is. The voice half is NOT re-read here — it keeps its own
    // subscription, so doing it here as well would read the voice position
    // twice on every notification.
    resyncAppliedMusic();

    const editor = editorStore.editor;
    if (!editor) return undefined;

    const observer: EditorObserver = {
      onEditorChange: () => resyncAppliedMusic(),
    };
    editor.registerObserver(observer, TIMELINE_MAY_HAVE_MOVED);
    return () => editor.unregisterObserver(observer, TIMELINE_MAY_HAVE_MOVED);
  }, [editorStore, resyncAppliedMusic]);

  /**
   * The selected language wins whenever the story has a dub for it; otherwise
   * the dub already on the timeline keeps its control, so a person can always
   * change what they are hearing.
   */
  const selectedHasDub =
    !!selectedLanguage && dubbedLanguages.includes(selectedLanguage);
  const controlLanguage = selectedHasDub
    ? selectedLanguage
    : (appliedDub.language ?? selectedLanguage);

  // Keeps the editor's in-memory description filled for the language the
  // timeline is set to, which is what the dub engine builds the audio from.
  const { status: descriptionStatus, refreshNow } = useDubDescriptionRefresh({
    editor: editorStore.editor as VideoEditor | undefined,
    storyId,
    language: appliedDub.mode === 'original' ? null : appliedDub.language,
    isOriginalVideoElement: probe => videoCreator.isOriginalVideoElement(probe),
    musicBedUrl: musicController?.bedUrl ?? null,
    voiceStemUrl:
      musicController?.voiceUrlMp3 ?? musicController?.voiceUrlWav ?? null,
    refreshSource: () => videoSourceStore.refreshSource(),
    enabled: dubEntryEnabled,
  });

  /**
   * One press, whichever control it came from. Both positions are passed in as
   * arguments, so neither can be read back off the screen after the fact.
   *
   * There is nothing on the network behind a press: it writes three values and
   * returns. The description the audio is built from is fetched separately and
   * left in the editor's holder, and its arrival asks for a new document.
   */
  const write = useCallback(
    (
      mode: AudioMode,
      musicMode: DubMusicMode,
      language: string | null,
      {
        voiceVolume,
        originalVolume,
      }: Pick<StoryMix, 'voiceVolume' | 'originalVolume'>,
    ) => {
      const editor = editorStore.editor as VideoEditor | undefined;
      if (!editor) return;

      // The cast is for `executeCommand`, which on this screen is the method
      // `VideoCreatorStore` replaced — and that replacement is what puts the
      // press on the person's undo list and into the next save.
      const ran = editor.executeCommand(
        new SetDubOptionsCommand(editor, {
          language,
          voiceMode: mode,
          musicMode,
          voiceVolume,
          originalVolume,
        }),
      );
      if (!ran) {
        logger.error('Could not write the dub options onto the story', {
          language,
          mode,
        });
        videoCreator.showToast({
          state: 'error',
          message: 'Could not apply the dub. Please try again.',
        });
        return;
      }

      // A press that asked to HEAR a dub is waiting on an answer; a press back
      // to the original voice is waiting on nothing. See the effect below.
      setAwaitingAnswer(mode === 'original' ? null : language);

      resyncAppliedDub();
      resyncAppliedMusic();
      videoSourceStore.refreshSource();
    },
    [
      editorStore,
      resyncAppliedDub,
      resyncAppliedMusic,
      videoCreator,
      videoSourceStore,
    ],
  );

  /**
   * The one failure a person still needs told about: they PRESSED for a dub the
   * story does not have.
   *
   * Said when the answer arrives rather than at the moment of the press,
   * because a press fetches nothing. Tied to a press, not to the state: a
   * timeline merely OPENED on a language whose dub the story has since lost
   * says nothing, because the person hears their own speech and a sentence
   * about something they did not just do would be noise.
   */
  const [awaitingAnswer, setAwaitingAnswer] = useState<string | null>(null);
  useEffect(() => {
    if (descriptionStatus !== 'missing') return;
    if (!appliedDub.language || awaitingAnswer !== appliedDub.language) return;
    setAwaitingAnswer(null);

    const message = STORY_DUB_APPLY_MESSAGES['no-dub'];
    if (message) videoCreator.showToast({ state: 'error', message });
  }, [appliedDub.language, awaitingAnswer, descriptionStatus, videoCreator]);

  // Each control moves its own position and passes the other as the TIMELINE
  // has it. Passing a fixed value instead silently clears the other. Music is
  // read at press time: a first dub's success handler is held from before
  // Keep Original Music wrote it.
  const onAudioModeChange = useCallback(
    (mode: AudioMode) => {
      const mix = readStoryMix();
      write(mode, mix.musicMode, controlLanguage, mix);
    },
    [controlLanguage, readStoryMix, write],
  );

  const onMusicModeChange = useCallback(
    (musicMode: DubMusicMode) => {
      write(appliedDub.mode, musicMode, controlLanguage, appliedMix);
    },
    [appliedDub.mode, appliedMix, controlLanguage, write],
  );

  // PLAT-11899 / PLAT-11927 — the levels alone move, on the language the
  // timeline plays: setting one must never switch the dub to the language
  // being read.
  const onLevelsChange = useCallback(
    (levels: Partial<DubLevels>) => {
      const next = {
        voiceVolume:
          levels.voiceVolume === undefined
            ? appliedMix.voiceVolume
            : normalizeDubVoiceVolume(levels.voiceVolume),
        originalVolume:
          levels.originalVolume === undefined
            ? appliedMix.originalVolume
            : normalizeDubVoiceVolume(levels.originalVolume),
      };
      if (
        next.voiceVolume === appliedMix.voiceVolume &&
        next.originalVolume === appliedMix.originalVolume
      ) {
        return;
      }
      write(appliedDub.mode, appliedMusic, appliedDub.language, next);
    },
    [appliedDub.language, appliedDub.mode, appliedMix, appliedMusic, write],
  );
  const onVoiceVolumeChange = useCallback(
    (level: number) => onLevelsChange({ voiceVolume: level }),
    [onLevelsChange],
  );
  const onOriginalVolumeChange = useCallback(
    (level: number) => onLevelsChange({ originalVolume: level }),
    [onLevelsChange],
  );

  // Neither position moves: only the description behind them is fetched again.
  const reapplyComposition = useCallback(() => {
    // A timeline on Original carries no dub audio, so there is nothing on it
    // that the server's new lines make wrong.
    if (appliedDub.mode === 'original') return;
    refreshNow();
  }, [appliedDub.mode, refreshNow]);

  return {
    audioMode: appliedDub.mode,
    appliedDubLanguage: appliedDub.language,
    controlLanguage,
    // Held while either read a press decides from is still out: the
    // dubbed-language list picks WHICH language is written, and the separation
    // state says whether there is a bed at all.
    audioModeBusy: !dubbedLanguagesSettled || !musicStateSettled,
    onAudioModeChange: dubEntryEnabled ? onAudioModeChange : undefined,
    onMusicModeChange: dubEntryEnabled ? onMusicModeChange : undefined,
    musicMode: appliedMusic,
    voiceVolume: appliedMix.voiceVolume ?? FULL_DUB_LEVEL,
    onVoiceVolumeChange: dubEntryEnabled ? onVoiceVolumeChange : undefined,
    originalVolume: appliedMix.originalVolume ?? FULL_DUB_LEVEL,
    onOriginalVolumeChange: dubEntryEnabled
      ? onOriginalVolumeChange
      : undefined,
    onLevelsChange: dubEntryEnabled ? onLevelsChange : undefined,
    dubbedLanguages,
    reapplyComposition: dubEntryEnabled ? reapplyComposition : undefined,
    refreshDubbedLanguages: dubEntryEnabled
      ? refreshDubbedLanguages
      : undefined,
  };
}
